/** @prettier */
import { ReinforcementQuestion } from "../../Question/questionApi";
import { API_ROOT, makeRequest } from "../../services/qbankApi";
import { TestMode } from "../models";

export type TestData = {
    uuid: string;
    name: string;
    numQuestions: number;
    mode: TestMode;
    timeRemainingInSeconds: number;
};

export type QuestionData = {
    id: string;
    text: string;
    flagged: boolean;
    answerChoices: string[];
    selectedAnswer: "none" | number;
};

type RawQuestionData = {
    uuid: string;
    answerChoices: string[];
    flagged: boolean;
    questionStem: string;
    selectedChoice?: string;
};

export async function startTest(testId: string): Promise<void> {
    const url = `${API_ROOT}tests/${testId}/start`;
    await makeRequest("PATCH", url);
}
export async function pauseTest(testId: string): Promise<void> {
    const url = `${API_ROOT}tests/${testId}/pause`;
    await makeRequest("PATCH", url, {});
}
export async function finishTest(testId: string): Promise<void> {
    const url = `${API_ROOT}tests/${testId}/finalize`;
    await makeRequest("PATCH", url, {});
}

type TestDataAndQuestions = TestData & {
    questions: QuestionData[];
};
export async function getTestAndQuestions(
    testId: string,
): Promise<TestDataAndQuestions> {
    const url = `${API_ROOT}tests/${testId}/questions`;
    const res = (await (await makeRequest("GET", url)).json()) as TestData & {
        questions: RawQuestionData[];
    };
    const questions = res.questions.map<QuestionData>((data) => ({
        id: data.uuid,
        text: data.questionStem,
        flagged: data.flagged,
        answerChoices: data.answerChoices,
        selectedAnswer: data.selectedChoice
            ? data.answerChoices.indexOf(data.selectedChoice)
            : "none",
    }));
    return {
        questions,
        uuid: res.uuid,
        name: res.name,
        numQuestions: res.numQuestions,
        mode: res.mode,
        timeRemainingInSeconds: res.timeRemainingInSeconds,
    };
}

type QuestionReviewFields = {
    keyConcept: string;
    correctAnswerExplanation: string;
    incorrectAnswerExplanation: string;
};
type RawReviewQuestionData = RawQuestionData &
    QuestionReviewFields & {
        correctAnswerChoice: string;
        reinforcements: ReinforcementQuestion[];
        references: Array<{ reference: string }>;
    };
export type ReviewQuestionData = QuestionData &
    QuestionReviewFields & {
        correctAnswer: string;
        reinforcementQuestion: ReinforcementQuestion | undefined;
        references: string[];
    };

export async function getTestReview(
    testId: string,
): Promise<TestData & { questions: ReviewQuestionData[] }> {
    const url = `${API_ROOT}tests/${testId}/questions`;
    const res = (await (await makeRequest("GET", url)).json()) as TestData & {
        questions: RawReviewQuestionData[];
    };
    const questions = res.questions.map<ReviewQuestionData>((data) => ({
        id: data.uuid,
        text: data.questionStem,
        flagged: data.flagged,
        answerChoices: data.answerChoices,
        selectedAnswer: data.selectedChoice
            ? data.answerChoices.indexOf(data.selectedChoice)
            : "none",
        correctAnswer: data.correctAnswerChoice,
        keyConcept: data.keyConcept,
        correctAnswerExplanation: data.correctAnswerExplanation,
        incorrectAnswerExplanation: data.incorrectAnswerExplanation,
        reinforcementQuestion: data.reinforcements[0],
        references: data.references.map(({ reference }) => reference),
    }));
    return {
        questions,
        uuid: res.uuid,
        name: res.name,
        numQuestions: res.numQuestions,
        mode: res.mode,
        timeRemainingInSeconds: res.timeRemainingInSeconds,
    };
}

export async function selectAnswer(
    testId: string,
    questionId: string,
    answer: string,
) {
    const url = `${API_ROOT}tests/${testId}/questions/${questionId}/answer`;
    await makeRequest("PATCH", url, {
        choice: answer,
    });
}

export async function deselectAnswer(testId: string, questionId: string) {
    const url = `${API_ROOT}tests/${testId}/questions/${questionId}/unanswer`;
    await makeRequest("PATCH", url);
}

export async function toggleFlaggedQuestion(
    testId: string,
    questionId: string,
) {
    const url = `${API_ROOT}tests/${testId}/questions/${questionId}/flag`;
    await makeRequest("PATCH", url);
}

export async function sendHeartbeat() {
    const url = `${API_ROOT}tests/alive`;
    await makeRequest("PATCH", url);
}

export async function getInProgressTest() {
    const url = `${API_ROOT}tests/inprogress`;
    try {
        const res = await makeRequest("GET", url);
        const { uuid } = (await res.json()) as TestData;
        return uuid;
    } catch (e) {
        if (e.statusCode === 404) {
            return undefined;
        }
        throw e;
    }
}
